<template>
  <div class="grid-view__head">
    <div
      v-for="groupBy in includeGroupBy ? activeGroupBys : []"
      :key="'field-group-' + groupBy.field"
      class="grid-view__head-group"
      :style="{ width: groupBy.width + 'px' }"
      :set="field = $options.methods.getField(allFieldsInTable, groupBy)"
    >
      <div v-if="field" class="grid-view__group-cell">
        <div class="grid-view__group-name">
          {{ field.name }}
        </div>
      </div>
    </div>
    <div
      v-if="includeRowDetails"
      class="grid-view__column grid-view__column--no-border-right"
      :style="{ width: gridViewRowDetailsWidth + 'px', display: 'flex', 'align-items': 'center', 'justify-content': 'center', position: 'relative' }"
    >
      <div class="grid-view__row-info-select-all" style="display: flex; align-items: center; justify-content: center; height: 100%; width: 100%;">
        <Checkbox
          :checked="areAllRowsSelected"
          :indeterminate="areSomeRowsSelected"
          :size="'small'"
          @input="toggleSelectAllRows"
        ></Checkbox>
      </div>
      <GridViewRowIdentifierDropdown
        v-if="
          !readOnly &&
          $hasPermission(
            'database.table.view.update',
            view,
            database.workspace.id
          )
        "
        :row-identifier-type-selected="view.row_identifier_type"
        @change="onChangeIdentifierDropdown"
        style="position: absolute; right: 2px; top: 50%; transform: translateY(-50%) scale(0.8); z-index: 1;"
      ></GridViewRowIdentifierDropdown>
    </div>
    <GridViewFieldType
      v-for="field in visibleFields"
      :key="'field-type-' + field.id"
      :database="database"
      :table="table"
      :view="view"
      :field="field"
      :all-fields-in-table="allFieldsInTable"
      :filters="view.filters"
      :read-only="readOnly"
      :store-prefix="storePrefix"
      @refresh="$emit('refresh', $event)"
      @dragging="$emit('dragging', $event)"
      @field-created="$emit('field-created', $event)"
      @move-field="moveField"
    ></GridViewFieldType>
    <div
      v-if="
        includeAddField &&
        !readOnly &&
        $hasPermission(
          'database.table.create_field',
          table,
          database.workspace.id
        )
      "
      class="grid-view__column"
      :style="{ width: 100 + 'px' }"
    >
      <!-- Origem Flow: sem @click no <a> — este bloco perde a hidratação
           neste fork e o listener do Vue nunca ancora; o clique é tratado
           por um fallback DOM no plugin theme.js, que reusa o fluxo
           "Inserir à direita" da última coluna. -->
      <a
        ref="createFieldContextLink"
        class="grid-view__add-column"
        data-highlight="add-field"
      >
        <i class="grid-view__add-column-icon iconoir-plus"></i>
      </a>
    </div>
    <!-- Origem Flow: o CreateFieldContext fica FORA do v-if de permissão
         de cima — aquele bloco diverge entre SSR e cliente (mismatch de
         hidratação) e o context nunca montava, deixando o "+" morto.
         Aqui só depende da prop estática includeAddField (true apenas na
         seção direita). -->
    <CreateFieldContext
      v-if="includeAddField"
      ref="createFieldContext"
      :table="table"
      :view="view"
      :all-fields-in-table="allFieldsInTable"
      :database="database"
      @field-created="$emit('field-created', $event)"
      @field-created-callback-done="afterFieldCreatedUpdateFieldOptions"
      @shown="onShownCreateFieldContext"
    ></CreateFieldContext>
  </div>
</template>

<script>
import { notifyIf } from '@baserow/modules/core/utils/error'
import CreateFieldContext from '@baserow/modules/database/components/field/CreateFieldContext'
import GridViewFieldType from '@baserow/modules/database/components/view/grid/GridViewFieldType'
import gridViewHelpers from '@baserow/modules/database/mixins/gridViewHelpers'
import viewHelpers from '@baserow/modules/database/mixins/viewHelpers'
import GridViewRowIdentifierDropdown from '@baserow/modules/database/components/view/grid/GridViewRowIdentifierDropdown'

export default {
  name: 'GridViewHead',
  components: {
    GridViewRowIdentifierDropdown,
    GridViewFieldType,
    CreateFieldContext,
  },
  mixins: [gridViewHelpers, viewHelpers],
  props: {
    visibleFields: {
      type: Array,
      required: true,
    },
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    view: {
      type: Object,
      required: true,
    },
    allFieldsInTable: {
      type: Array,
      required: true,
    },
    includeRowDetails: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    includeGroupBy: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    includeAddField: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    includeGridViewIdentifierDropdown: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    readOnly: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['dragging', 'field-created', 'refresh'],
  computed: {
    allRows() {
      return this.$store.getters[this.storePrefix + 'view/grid/getAllRows']
    },
    checkboxSelectedRows() {
      return this.$store.state[this.storePrefix + 'view/grid'].checkboxSelectedRows
    },
    areAllRowsSelected() {
      if (this.allRows.length === 0) return false
      return this.allRows.every((row) =>
        this.checkboxSelectedRows.includes(row.id)
      )
    },
    areSomeRowsSelected() {
      if (this.areAllRowsSelected) return false
      return this.allRows.some((row) =>
        this.checkboxSelectedRows.includes(row.id)
      )
    },
  },
  methods: {
    toggleSelectAllRows() {
      if (this.areAllRowsSelected) {
        this.$store.dispatch(
          this.storePrefix + 'view/grid/clearCheckboxSelections'
        )
        this.$store.commit(
          this.storePrefix + 'view/grid/SET_MULTISELECT_ACTIVE',
          false
        )
        this.$store.commit(
          this.storePrefix + 'view/grid/SET_SELECTION_TYPE',
          null
        )
      } else {
        const rowIds = this.allRows.map((row) => row.id)
        this.$store.dispatch(
          this.storePrefix + 'view/grid/selectAllCheckboxRows',
          { rowIds }
        )
      }
    },
    /**
     * After newField is created pressing "insert left" or "insert right" button,
     * we need to move the newField into the correct order position.
     * Also necessary when we duplicate a field.
     * This function move the field thanks to the store.
     **/
    async moveField({
      position,
      newField,
      fromField,
      undoRedoActionGroupId = null,
      visible = null,
    }) {
      try {
        await this.$store.dispatch(
          `${this.storePrefix}view/grid/updateSingleFieldOptionOrder`,
          {
            fieldToMove: newField,
            position,
            fromField,
            undoRedoActionGroupId,
            readOnly: this.readOnly,
            visible,
          }
        )
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    async onChangeIdentifierDropdown(rowIdentifierType) {
      try {
        await this.$store.dispatch('view/update', {
          view: this.view,
          values: { row_identifier_type: rowIdentifierType },
        })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    onShownCreateFieldContext() {
      this.$refs.createFieldContext.showFieldTypesDropdown(this.$el)
    },
    getField(allFieldsInTable, groupBy) {
      const field = allFieldsInTable.find((f) => f.id === groupBy.field)
      return field
    },
  },
}
</script>
